import type { ReactElement } from "react";
import { useState, useEffect } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import MultiChipDownStateless from "pro-features/components/MultiChipDown/src/MultiChipDownStateless";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { ITEMS_RENDER_BUFFER, ITEMS_RENDER_LIMIT, MAX_SELECTED_DOMAINS } from "./Consts";
import type { IChipItemWithOptionType, IDomainsFilterProps, IDomainsList } from "./Types";
import {
    getFilteredDomain,
    createDomainsStringIds,
    onItemClick,
    renderChipTitleText,
    renderDropDownItem,
    transformDomainsData,
    createUniqueListOfAllDomains,
} from "./Utils";

export const DomainsFilter: React.FC<IDomainsFilterProps> = ({ activationSets, domainsFilterData }) => {
    const { params } = useCurrentStateAndParams();
    const { domains } = params;
    const [selectedItems, setSelectedItems] = useState<IChipItemWithOptionType[]>([]);
    const [wereChangesMade, setWereChangesMade] = useState<boolean>(false);
    const [domainsList, setDomainsList] = useState<IDomainsList[]>([]);

    useEffect(() => {
        resetState();
    }, [domainsList]);

    useEffect(() => {
        const filterList = async () => {
            const domains = transformDomainsData(domainsFilterData);
            const combinedData = domains && activationSets && getFilteredDomain(domains, activationSets);
            setDomainsList(combinedData);
        };
        filterList();
    }, [domainsFilterData, activationSets]);

    const resetState = () => {
        if (domains && domainsList?.length > 0) {
            const domainItems = domains.split(",");
            const cleanedDomains = domainItems.map((domain: string) =>
                domain.startsWith("*") ? domain.substring(1) : domain,
            );
            const items = domainsList.filter((item) => cleanedDomains.includes(item.id));
            setSelectedItems(items);
        }
    };

    const onChipClicked = (item: IChipItemWithOptionType) => {
        const newSelectedItems = onItemClick(item, selectedItems, domainsList);
        setSelectedItems(newSelectedItems);

        setWereChangesMade(true);
    };

    const clearSelection = () => {
        setSelectedItems([]);
        proNavigator.updateParams({ ...params, domains: null });
    };

    const onDone = () => {
        if (!wereChangesMade) {
            return;
        }
        const domainsStringIds = createDomainsStringIds(selectedItems, domainsList, activationSets);
        const allDomains = createUniqueListOfAllDomains(domainsStringIds.split(","), activationSets);
        setWereChangesMade(false);
        allTrackers.dispatchTrackEvent("Drop Down", "submit-ok", `${allDomains ?? "No Domains/Project Selected"}`);
        proNavigator.updateParams({
            ...params,
            domains: domainsStringIds,
        });
    };

    const searchFilter = (term: string, item: ReactElement): boolean => {
        // @ts-expect-error error TS2339: Property 'children' does not exist on type 'unknown'.
        return (item.props.iconName ? (item.props.children.props.children[0].props.id as string) : item.props.id)
            .toLowerCase()
            .startsWith(term.toLowerCase());
    };

    const onToggleDropdown = (isOpen: boolean) => {
        if (isOpen) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", `Domain/Project filter opened`);
        }
    };
    return (
        <MultiChipDownStateless
            hasSearch
            searchFilter={searchFilter}
            searchPlaceHolder={translate("keywords.analysis.filters.domains.projects.searchPlaceHolder")}
            data-automation="keyword-domains-multi-chipdown"
            cssClassContainer="DropdownContent-container keyword-domains-multi-chipdown"
            dropdownWidth={280}
            dropdownPopupHeight={300}
            options={domainsList ?? []}
            selectedItems={selectedItems}
            buttonText={translate("keywords.analysis.filters.domains.projects.button")}
            getDropdownItem={renderDropDownItem(selectedItems, domainsList, activationSets)}
            onItemClick={onChipClicked}
            onCloseItem={clearSelection}
            renderChipTitleText={() => renderChipTitleText(selectedItems, activationSets)}
            onDone={onDone}
            onClickOutside={onDone}
            onToggleDropdown={onToggleDropdown}
            showApplyButton={selectedItems.length <= MAX_SELECTED_DOMAINS}
            virtualize={domainsList?.length > ITEMS_RENDER_LIMIT ? { itemBuffer: ITEMS_RENDER_BUFFER } : undefined}
            maxVirtualItemsToRender={domainsList?.length > ITEMS_RENDER_LIMIT ? ITEMS_RENDER_LIMIT : undefined}
        />
    );
};
